import createServiceStore from 'shared/utils/createServiceStore.js';
import { DESTINATIONS_SERVICE_KEY } from './destinationsEnum.js';
import destinationsService from './destinationsService.js';
import { setLoading } from '../loading/loadingMutationTypes.js';
import { setError } from '../error/errorMutationTypes.js';
import {
  changeDestinationStatusAction,
  testDestinationConnectionAction,
  testExistingDestinationConnectionAction,
} from 'web/destinations/destinationsActionTypes';
import { setDestinationTestConnectionResult } from 'web/destinations/destinationsMutationTypes';
import destinationsServiceActions from 'web/destinations/destinationsServiceActions';
import { forEach, get, last, capitalize } from 'lodash';
import { destinationKeysMappings } from './destinationKeysMappings.js';

export default createServiceStore({
  serviceName: DESTINATIONS_SERVICE_KEY,
  service: destinationsService,
  setLoadingMutation: setLoading,
  setErrorMutation: setError,
  actions: {
    async [testDestinationConnectionAction]({ commit }, { data, config }) {
      commit(setLoading, { key: testDestinationConnectionAction, value: true });
      commit(setError, { key: testDestinationConnectionAction, value: null });
      commit(setError, { key: destinationsServiceActions.create, value: null });
      commit(setDestinationTestConnectionResult, {});

      try {
        const result = await destinationsService.testConnection({
          data: { ...data, db: data.data_store },
          ...config,
        });
        commit(setDestinationTestConnectionResult, result);
      } catch (err) {
        //Handle 422 errors that are due to schema validation errors
        if (get(err, 'response.status') === 422) {
          let schemaErrors = [];
          const destinationType = get(data, 'config.destination_type', '');
          if (!destinationType) {
            schemaErrors.push({
              success: false,
              action: 'Check Destination Configuration',
              message:
                'Please verify the destination configuration, and make sure all required fields are filled',
              mandatory: true,
            });
          } else {
            const errorMessages = get(
              err,
              'response.data.detail',
              'Invalid credentials',
            );
            forEach(errorMessages, (errorMessage) => {
              const configKey = last(get(errorMessage, 'loc', []));
              const configKeyLabel = get(destinationKeysMappings, [
                destinationType,
                configKey,
              ]);
              if (configKeyLabel) {
                const message = capitalize(get(errorMessage, 'msg', ''));
                schemaErrors.push({
                  success: false,
                  action: `Verify "${configKeyLabel}" Field`,
                  message: message,
                  mandatory: true,
                });
              }
            });
          }
          commit(setDestinationTestConnectionResult, {
            results: schemaErrors,
            success: false,
          });
        } else {
          commit(setError, {
            key: testDestinationConnectionAction,
            value: err,
          });
          throw err;
        }
      } finally {
        commit(setLoading, {
          key: testDestinationConnectionAction,
          value: false,
        });
      }
    },
    async [testExistingDestinationConnectionAction](
      { commit },
      { id, data = {} } = {},
    ) {
      commit(setLoading, {
        key: testExistingDestinationConnectionAction,
        value: true,
      });
      commit(setError, {
        key: testExistingDestinationConnectionAction,
        value: null,
      });
      commit(setError, { key: destinationsServiceActions.create, value: null });
      commit(setDestinationTestConnectionResult, {});

      try {
        const result =
          await destinationsService.testExistingDestinationConnection(id, {
            data: {
              config: data.config,
              ...(data.tunnel_id ? { tunnel_id: data.tunnel_id } : {}),
            },
          });
        commit(setDestinationTestConnectionResult, result);
      } catch (err) {
        //Handle 422 errors that are due to schema validation errors
        if (get(err, 'response.status') === 422) {
          let schemaErrors = [];
          const destinationType = get(data, 'config.destination_type', '');
          if (!destinationType) {
            schemaErrors.push({
              success: false,
              action: 'Check Destination Configuration',
              message:
                'Please verify the destination configuration, and make sure all required fields are filled',
              mandatory: true,
            });
          } else {
            const errorMessages = get(
              err,
              'response.data.detail',
              'Invalid credentials',
            );
            forEach(errorMessages, (errorMessage) => {
              const configKey = last(get(errorMessage, 'loc', []));
              const configKeyLabel = get(destinationKeysMappings, [
                destinationType,
                configKey,
              ]);
              if (configKeyLabel) {
                const message = capitalize(get(errorMessage, 'msg', ''));
                schemaErrors.push({
                  success: false,
                  action: `Verify "${configKeyLabel}" Field`,
                  message: message,
                  mandatory: true,
                });
              }
            });
          }
          commit(setDestinationTestConnectionResult, {
            results: schemaErrors,
            success: false,
          });
        } else {
          commit(setError, {
            key: testExistingDestinationConnectionAction,
            value: err,
          });
          throw err;
        }
      } finally {
        commit(setLoading, {
          key: testExistingDestinationConnectionAction,
          value: false,
        });
      }
    },
    async [changeDestinationStatusAction](
      { commit },
      { destinationId, status, silent = false },
    ) {
      !silent &&
        commit(setLoading, {
          key: changeDestinationStatusAction,
          value: status,
        });
      commit(setError, { key: changeDestinationStatusAction, value: null });

      try {
        await destinationsService.changeStatus(destinationId, status);
      } catch (err) {
        commit(setError, { key: changeDestinationStatusAction, value: err });
        throw err;
      } finally {
        !silent &&
          commit(setLoading, {
            key: changeDestinationStatusAction,
            value: false,
          });
      }
    },
  },
});
